import type { Category, CategoryNode } from './categoryTree'

/*
 * Sorting and filtering for the categories tree.
 *
 * Lifted out of `CategoryTable.tsx` on 2026-08-21 so `useCategoryView` can
 * derive the same segments the table renders. Nothing here changed in the
 * move — it is the identical code, exported rather than file-local, and the
 * table now imports what it used to declare.
 */

/**
 * The orders this table can actually put rows in.
 *
 * **Both of the design's other two are gone rather than shown inert.** They
 * were drawn as dimmed segments with a tooltip, on the reasoning that their
 * absence was more confusing than their presence; that was wrong, and it read
 * as a broken control instead.
 *
 * - **Rules** is dropped outright. It needs the auto-rules module
 *   (#287/#288), and nothing on the roadmap makes it a sort before the column
 *   itself exists.
 * - **Most used** is BACK, on backend 270's `most_used`. It was dropped because the
 *   only count in the schema was `CategorySpending` inside
 *   `RecurringSummaryResponse`, which counts recurring items for M7 —
 *   sorting by it would have ranked categories by their subscriptions.
 *   `CategoryList.most_used` is the real thing: ids, most-tagged first.
 *
 *   **It ranks 8 rows out of 262, and the segment is honest about only that
 *   much.** The eight lead, in rank order; everything else follows A–Z rather
 *   than in whatever order the engine leaves behind. The alternative
 *   considered was a filter showing only the eight, which matches the data
 *   exactly — the sort was chosen because it is the control the design drew
 *   and it keeps every category reachable from one list.
 *
 *   **Absent, not inert, when there is no ranking.** A caller with no
 *   transactions gets `most_used: []`, and so does a backend that predates
 *   the field. Rendering the segment then would be the dimmed-control mistake
 *   this docblock already records — it would highlight and reorder nothing.
 *
 * `Recently added` is in its place because it is real — `created_at` is on
 * every `CategoryResponse` — and because it earns its keep at 88 rows: it is
 * how you find the category you made a minute ago without knowing where it
 * sorts alphabetically.
 */
export const SORTS = [
  { key: 'used', label: 'Most used' },
  { key: 'name', label: 'A–Z' },
  { key: 'recent', label: 'Recently added' },
] as const

export type SortKey = (typeof SORTS)[number]['key']

/** `most_used` ids → position, so a lookup is O(1) per row rather than an
 *  `indexOf` inside a comparator. */
export type Ranking = ReadonlyMap<string, number>

/** One shared empty, so the default prop is a stable reference and the memos
 *  below do not rebuild on every render of a caller that passes nothing. */
export const NO_RANKING: readonly string[] = []

/**
 * A top-level row's rank is the BEST rank in its subtree, not its own.
 *
 * `most_used` ranks whatever the user tags, and what they tag is almost always
 * a subcategory — `Groceries`, not `Food & Drinks`. Ranking a parent only by
 * its own id would leave the row containing your most-used category sitting in
 * the alphabetical tail, which is the opposite of what the control is for.
 *
 * The children keep their own order inside the expanded row. Reordering them
 * too would move a subcategory away from the position the user just learned it
 * sits at, to save one line of scanning in a list of at most eleven.
 */
function bestRank(node: CategoryNode, rank: Ranking): number {
  let best = rank.get(node.category.id) ?? Infinity
  for (const child of node.children) best = Math.min(best, rank.get(child.id) ?? Infinity)
  return best
}

/**
 * Newest first, and ties broken by NAME rather than left to the engine.
 *
 * `created_at` is a second-resolution timestamp and the 88 system categories
 * are seeded in one transaction, so most of them share it exactly. Without a
 * tiebreak the order within that block is whatever the sort happens to do,
 * which is stable per-run and arbitrary between them — a list that reshuffles
 * for no reason the user can see.
 */
function compare(a: Category, b: Category, sort: SortKey): number {
  if (sort === 'name') return a.name.localeCompare(b.name)
  return b.created_at.localeCompare(a.created_at) || a.name.localeCompare(b.name)
}

/** The node-level comparator, because `used` needs a row's children and the
 *  other two only ever need the row. */
export function compareNodes(a: CategoryNode, b: CategoryNode, sort: SortKey, rank: Ranking): number {
  if (sort !== 'used') return compare(a.category, b.category, sort)
  const ra = bestRank(a, rank)
  const rb = bestRank(b, rank)
  // Two branches rather than `ra - rb || …`, and the reason is legibility
  // rather than correctness — checked, because the first draft of this comment
  // claimed correctness and was wrong. The unranked tail is all `Infinity` and
  // `Infinity - Infinity` is NaN, but **NaN is falsy**, so the `||` form falls
  // through to the name compare exactly as this does. Mutating it to that form
  // leaves all 24 tests green. Spelling it out just saves the next reader from
  // having to work that out.
  if (ra !== rb) return ra - rb
  return a.category.name.localeCompare(b.category.name)
}

/** Case- and space-insensitive, matching the collision check in `CategoryForm`. */
const normalise = (s: string) => s.trim().toLowerCase()

/**
 * A node survives the filter if IT matches, or if any of its children do.
 *
 * Keeping a parent whose child matched is what makes the result readable — a
 * bare list of matching subcategories loses which category each belongs to,
 * and the parentage is the table's whole structure. A kept parent shows only
 * its matching children, unless the parent itself matched, in which case all
 * of them are its context.
 */
export function filterNodes(nodes: CategoryNode[], query: string): CategoryNode[] {
  const q = normalise(query)
  if (q === '') return nodes
  const hit = (c: Category) => normalise(c.name).includes(q)

  return nodes.flatMap((node) => {
    if (hit(node.category)) return [node]
    const children = node.children.filter(hit)
    return children.length > 0 ? [{ category: node.category, children }] : []
  })
}
